import React, { useEffect, useState } from 'react';
import { oneOfType, func, node } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import useUser from 'Hooks/useUser';
import useRouter from 'Hooks/useRouter';
import useFetch from 'Hooks/useFetch';
import {
  API_PASSWORD_RECOVERY,
  API_AUTHENTICATION_LOGIN_VERIFY,
  required,
  minLength,
  maxLength,
} from 'Constants';

const defaultValues = {
  authCode: '',
};

const AuthHOC = ({ children }) => {
  const { data: user, userLogin, userLogout, mutate } = useUser();
  const fetcher = useFetch();
  const { push, location } = useRouter();
  const { t } = useTranslation();
  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    getValues,
    trigger,
    watch,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });
  const [state, setState] = useState({
    username: '',
    password: '',
    recovery_email: '',
  });
  const [recovered, setRecovered] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [err, setErr] = useState('');
  const [mfaVerifyError, setMfaVerifyError] = useState('');
  const [loadingVerify, setLoadingVerify] = useState(false);

  const authCodeValue = watch('authCode');
  const validators = {
    authCode: {
      required,
      minLength: minLength(6),
      maxLength: maxLength(6),
    }
  };

  useEffect(() => {
    if (
      user?.loggedIn
      && user?.userId
      && success
    ) {
      setSuccess(false);
      setLoading(false);
    }
  }, [user?.userId, success]);

  useEffect(() => {
    if (user?.sessionExpired && location.pathname !== '/login' && !loading) {
      setErr(err || t('common.sessionExpired'));
    } else {
      setErr(err);
    }
  }, [user?.sessionExpired, loading]);

  const handleOnLogin = async e => {
    e.preventDefault();
    const formEl = e.target.closest('form');

    if (!formEl.reportValidity()) {
      return;
    }

    try {
      setLoading(true);
      setErr('');
      const response = await userLogin({ ...state, _remember_me: rememberMe });

      if (response?.success && response?.twoFactorAuthenticationEnabled === false) {
        await mutate({
          ...user,
          hasTwoFactorAuthenticationEnabled: false,
          sessionExpired: false
        }, false);

        push('/mfa');
      }

      if (response?.error) {
        setErr(response.message);
      }
    } catch (error) {
      setSuccess(false);
      setErr(error?.info?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOnChange = type => event => {
    setErr('');
    setState(prevState => ({
      ...prevState,
      [type]: event.target.value
    }));
  };

  const handleRememberMe = checked => {
    setRememberMe(checked);
  };

  const handleError = errValue => {
    setErr(errValue);
  };

  const handleOnRecoverPassword = async e => {
    e.preventDefault();
    const formEl = e.target.closest('form');

    if (!formEl.reportValidity()) {
      return;
    }

    try {
      setRecovered(false);
      setLoading(true);
      setErr('');
      const response = await fetch(API_PASSWORD_RECOVERY, {
        method: 'POST',
        body: JSON.stringify({ email: state.recovery_email })
      });
      if (
        response.status < 299
        && response.status >= 200
      ) {
        setRecovered(true);
        setState(prevState => ({
          ...prevState,
          recovery_email: ''
        }));
      } else {
        const body = await response.json();
        setErr(body || t('profile.passwordRecoveryError', { email: state.recovery_email }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnLoginVerify = async () => {
    await trigger();
    if (Object.keys(rhfErrors).length || authCodeValue === '') {
      return;
    }
    const authCode = getValues('authCode');
    setLoadingVerify(true);
    setMfaVerifyError('');
    fetcher(API_AUTHENTICATION_LOGIN_VERIFY, {
      method: 'POST',
      body: JSON.stringify({ code: authCode })
    })
      .then(async () => {
        await mutate({ isTwoFactorAuthenticationVerified: true, loggedIn: true }, true);
        reset(defaultValues);
        const locationState = location?.state || {};
        let redirectTo = locationState?.pathname;
        if (!redirectTo) {
          redirectTo = location.pathname;
        }
        if (redirectTo?.includes('login')) {
          redirectTo = null;
        }
        if (!redirectTo) {
          push('/');
        } else {
          push({
            pathname: redirectTo,
            search: location?.state?.search || location.search,
            hash: location?.state?.hash || location.hash
          });
        }
      })
      .catch(error => {
        setMfaVerifyError(error?.info?.message || error.message);
      })
      .finally(() => {
        setLoadingVerify(false);
      });
  };

  const propsToPass = {
    username: state.username,
    password: state.password,
    recoveryEmail: state.recovery_email,
    loading,
    sessionExpired: !!user?.sessionExpired,
    rememberMe,
    error: err,
    mfaVerifyError,
    loadingVerify,
    success,
    recovered,
    setRecovered,
    validators,
    register,
    rhfErrors,
    onRecoverPasswordSubmit: handleOnRecoverPassword,
    onRemember: handleRememberMe,
    onChange: handleOnChange,
    onSubmit: handleOnLogin,
    onError: handleError,
    onLoginVerify: handleOnLoginVerify,
    userLogout,
  };

  if (typeof children === 'function') {
    return children(propsToPass);
  }

  return React.cloneElement(children, propsToPass);
};

AuthHOC.propTypes = {
  children: oneOfType([func, node]),
};

export default AuthHOC;
